import { Card, CardContent } from "@volca/ui/components/ui/card";
import { Separator } from "@volca/ui/components/ui/separator";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { CopyField } from "./copy-field";
import { GuideRow } from "./guide-row";
import { FORWARDING_GUIDE } from "./guides";
import { GoogleMark, MicrosoftMark } from "./provider-marks";

// The provider's own instructions, kept on the step as well as inside the guide. Most
// customers know which of these they use and want to get straight to it.
const PROVIDER_GUIDES = [
  {
    label: "Outlook and Microsoft 365 forwarding instructions",
    href: "https://support.microsoft.com/en-us/outlook/mail/use-rules-to-automatically-forward-messages",
    Mark: MicrosoftMark,
  },
  {
    label: "Gmail forwarding instructions",
    href: "https://support.google.com/mail/answer/10957",
    Mark: GoogleMark,
  },
];

export const ForwardingOverview = ({
  address,
  byEmail = true,
}: {
  address: string | null;
  /**
   * False when the customer told us no invoices arrive by email. There is no rule to
   * make then, so the guide, the vendor advice and the providers' instructions are all
   * answers to a question they did not ask. The address stays: it is where they send
   * the ones they have.
   */
  byEmail?: boolean;
}) => (
  <Card className="w-full gap-0 py-0">
    <CardContent className="p-0">
      {byEmail && (
        <GuideRow
          guide={FORWARDING_GUIDE}
          title="1. Set up automatic forwarding"
          detail="Outlook and Gmail, step by step"
        />
      )}
      <Stack
        gap={4}
        align="stretch"
        className={cn("w-full", cn("p-6", { "border-t border-line": byEmail }))}
      >
        {/* No heading of its own: the page title already says what this step is for, and
            a card repeating it reads as a second page title. The field's own label is
            the only heading this card needs. This is the pane that sets forwarding up,
            so the address it forwards to belongs here and is offered nowhere else. */}
        {address ? (
          <CopyField label="Your Volca address" value={address} />
        ) : (
          <Text tone="muted">Your Volca address is not available yet.</Text>
        )}

        {!byEmail && (
          <Text tone="muted">
            You told us none of your invoices arrive by email, so there's no
            forwarding rule to set up. Send them to the address above, or upload
            them on the next step.
          </Text>
        )}

        {byEmail && <Separator />}

        {/* Most shops get the bulk of their invoices from a handful of vendors, so the
            step says to start there rather than implying an all-or-nothing switch. */}
        {byEmail && (
          <Stack className="w-full" gap={1} align="stretch">
            <Text weight="semibold" variant="caption">
              Start with your biggest vendors
            </Text>
            <Text tone="muted">
              You do not need every vendor on day one. Pick the handful that
              send you the most invoices, and add the rest later.
            </Text>
            <ol className="text-md list-decimal space-y-1 pl-5 text-subtle">
              <li>
                <Text tone="muted">
                  List the vendors who invoice you most often. Three or four is
                  plenty to begin with.
                </Text>
              </li>
              <li>
                <Text tone="muted">
                  In the mailbox those invoices arrive in, make one rule:
                  messages from those senders, with an attachment, go to your
                  Volca address.
                </Text>
              </li>
              <li>
                <Text tone="muted">
                  Keep your own copy. The rule sends us a copy, and every
                  invoice still lands in your inbox as it does today.
                </Text>
              </li>
              <li>
                <Text tone="muted">
                  Send the test below, then add more vendors to the same rule as
                  you go.
                </Text>
              </li>
            </ol>
          </Stack>
        )}

        {byEmail && (
          <Stack className="w-full" gap={1} align="stretch">
            <Text tone="muted">
              Forward only invoice messages. If your provider asks for a
              confirmation code, it comes to us and we show it to you below.
            </Text>
            {PROVIDER_GUIDES.map(({ label, href, Mark }) => (
              <a
                key={href}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-md flex w-fit items-center gap-2 text-link"
              >
                {/* The mark is what people scan for, so it sits outside the underline
                    rather than being dragged through it. */}
                <Mark className="h-4 w-4 shrink-0" />
                <span className="underline underline-offset-4">{label}</span>
              </a>
            ))}
          </Stack>
        )}
      </Stack>
    </CardContent>
  </Card>
);
